import { getProviderForUrl } from "unpic";
import { blobToBase64 } from "#lib/blob.js";
import { Logger } from "#lib/logging.js";
import { PUBLIC_CDN_URL, PUBLIC_GENERATOR_URL, PUBLIC_HOSTNAME } from "$app/env/public";

export const MAX_IMAGE_MEGAPIXELS = 100 * Math.pow(10, 6);

export const SVG_MIME_TYPE = "image/svg+xml";
export const PNG_MIME_TYPE = "image/png";

const CDN_PREFIX = `${PUBLIC_CDN_URL}/cdn-cgi/image/`;

export function getImageDimensions(image: File | string | null): Promise<{ width: number; height: number }> {
	if (!image) return Promise.reject(new Error("No image provided"));

	const isFile = image instanceof File;

	return new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => {
			if (isFile) URL.revokeObjectURL(img.src);
			resolve({ width: img.width, height: img.height });
		};
		img.onerror = () => reject(new Error("Failed to load image"));
		img.src = isFile ? URL.createObjectURL(image) : image;
	});
}

export function transformURL(url: string | undefined | null, options: { trim?: string; g?: string; w?: number; h?: number; f?: string } = { f: "auto" }): string {
	if (!url) {
		return "";
	}
	if (getProviderForUrl(url)) {
		return encodeURL(url);
	}
	if (url.indexOf("http://") === -1 && url.indexOf("https://") === -1) {
		url = PUBLIC_HOSTNAME + url;
	}
	const optionsString = Object.entries(options)
		.map(([key, value]) => `${key}=${value}`)
		.join(",");
	return `${CDN_PREFIX}${optionsString}/${isUrlEncoded(url) ? url : encodeURIComponent(url)}`;
}

const CDN_OPTION_ALIASES: Record<string, string> = { w: "width", h: "height", format: "f" };

export function resizeURL(url: string, options: Record<string, string | number>): string {
	const cdnUrl = /^(https?:\/\/[^/]+\/cdn-cgi\/image\/)([^/]*)\/(.+)$/i.exec(url);
	const prefix = cdnUrl?.[1] ?? CDN_PREFIX;
	let target = cdnUrl?.[3] ?? url;
	const existing = Object.fromEntries(
		(cdnUrl?.[2] ?? "")
			.split(",")
			.map(option => [option.substring(0, option.indexOf("=")), option.substring(option.indexOf("=") + 1)])
			.filter(([key]) => key)
			.map(([key, value]) => [CDN_OPTION_ALIASES[key] ?? key, value]),
	);
	if (!/^https?(:|%3A)/i.test(target)) {
		target = PUBLIC_HOSTNAME + target;
	}
	const optionsString = Object.entries({ ...existing, ...options })
		.map(([key, value]) => `${key}=${value}`)
		.join(",");
	return `${prefix}${optionsString}/${isUrlEncoded(target) ? target : encodeURIComponent(target)}`;
}

function encodeURL(url: string): string {
	if (!url) {
		return "";
	}
	if (url.startsWith(CDN_PREFIX)) {
		const path = url.substring(CDN_PREFIX.length);
		const index = path.indexOf("/");
		if (index !== -1) {
			const target = path.substring(index + 1);
			return `${CDN_PREFIX}${path.substring(0, index)}/${isUrlEncoded(target) ? target : encodeURIComponent(target)}`;
		}
	}
	return url;
}

function isUrlEncoded(str: string): boolean {
	try {
		const decodedStr = decodeURIComponent(str);
		return decodedStr !== str;
	} catch {
		return false;
	}
}

function isEpsFileName(url: string): boolean {
	// Match .eps with optional query string after
	return /\.eps(\?.*)?$/.test(url);
}

function isPDFFileName(url: string): boolean {
	// Match .pdf with optional query string after
	return /\.pdf(\?.*)?$/.test(url);
}

function isPostscriptApplicationType(url: string | null | undefined): boolean {
	// Match application/postscript or application/eps
	return /application\/postscript|application\/eps/.test(url ?? "");
}

function isPDFApplicationType(url: string | null | undefined): boolean {
	// Match application/pdf
	return /application\/pdf/.test(url ?? "");
}

export function bypassCropperForContentType(contentType: string | null | undefined): boolean {
	return isPDFApplicationType(contentType) || isPostscriptApplicationType(contentType);
}

export function requireImageConversion(url: string, contentType: string | null | undefined): boolean {
	return isPDFFileName(url) || isPDFApplicationType(contentType) || isEpsFileName(url) || isPostscriptApplicationType(contentType);
}

export async function convertPDFOrEPSImage(url: string, contentType: string): Promise<string | undefined> {
	let generatorConversionPath;
	if (isPDFFileName(url) || isPDFApplicationType(contentType)) {
		generatorConversionPath = "/v1/pdf";
	} else if (isEpsFileName(url) || isPostscriptApplicationType(contentType)) {
		generatorConversionPath = "/v1/eps";
	} else {
		return undefined;
	}

	try {
		const req = await fetch(`${PUBLIC_GENERATOR_URL}${generatorConversionPath}?url=${url}`, {
			method: "GET",
		});
		if (req.status !== 200) {
			throw new Error("Studio upload asset modal - error fetching EPS file preview");
		}
		const blob = await req.blob();
		return await blobToBase64(blob);
	} catch (e) {
		Logger.error("Error converting image", { err: e });
	}
}

export function setFormatOnURL(urlStr: string, format?: string): string {
	const cdn = getProviderForUrl(urlStr);
	if (cdn === "cloudflare") {
		return urlStr.replace(/[,]?f=auto/, format ?? "");
	}
	return urlStr;
}
